import React, { useState, useEffect } from "react";
import { FileUp, CheckCircle2, AlertCircle, Clock3, Sparkles, Download, ExternalLink, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
// import CommunityFeed from "./CommunityFeed";
import CoralReef from "../images/CoralReef.jpg";
import { request } from "../service/HttpService";
import { calculateOverallScore } from "../service/Operations";
import { fetchUserSettings } from "../service/UserSettingsService";
import { fetchPaperOfTheWeek, downloadFeaturedManuscript } from "../service/ManuscriptService";
import { useFeatureFlags } from "./FeatureFlagsContext";
import { getJwtRole } from "../service/AuthService";
import { admin, FEATURES } from "../constants/Constants";


function statusClass(status) {
    if (!status) return "badge-processing";
    const s = status.toLowerCase();
    if (s === "completed") return "badge-complete";
    if (s === "error") return "badge-error";
    return "badge-processing";
}

function StatusIcon({ status }) {
    if (!status) return <Clock3 size={12} />;
    const s = status.toLowerCase();
    if (s === "completed") return <CheckCircle2 size={12} />;
    if (s === "error") return <AlertCircle size={12} />;
    return <Clock3 size={12} />;
}

function statusLabel(status) {
    if (!status) return "Processing";
    const s = status.toLowerCase();
    if (s === "completed") return "Complete";
    if (s === "error") return "Error";
    return "Processing";
}

function PaperOfTheWeekCard({ paper, onDownload, onOpen, downloading, hiddenFromUsers }) {
    const overall = paper.overallScore ?? 0;
    const isShowcase = !!paper.showcaseKey;
    const initials = (paper.authorName ?? "AR")
        .split(" ")
        .map(p => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    return (
        <div
            className="potw-card"
            onClick={isShowcase ? onOpen : undefined}
            style={isShowcase ? { cursor: "pointer" } : undefined}
        >
            <div className="potw-aurora" />
            <div className="potw-main">
                <div className="potw-header">
                    <span className="potw-pill">
                        <Sparkles size={12} />
                        Paper of the Week
                    </span>
                    <span className="potw-caption">
                        {paper.fromThisWeek ? "Highest score this week" : "Most recent featured paper"}
                    </span>
                    {hiddenFromUsers && (
                        <span className="potw-caption" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#f5c451" }}>
                            <AlertTriangle size={12} />
                            Hidden from users
                        </span>
                    )}
                </div>
                <h3 className="potw-title">{paper.title || "Untitled manuscript"}</h3>
                <div className="potw-author-row">
                    <div className="potw-avatar">
                        {paper.authorImageUrl
                            ? <img src={paper.authorImageUrl} alt="" className="potw-avatar-img" />
                            : <span>{initials}</span>}
                    </div>
                    <div>
                        <p className="potw-author-name">{paper.authorName ?? "Anonymous Researcher"}</p>
                        {paper.uploadDate && (
                            <p className="potw-author-meta">
                                Submitted {new Date(paper.uploadDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </p>
                        )}
                    </div>
                </div>
                <div className="potw-scores">
                    <div className="potw-score">
                        <span className="potw-score-dot" style={{ background: "#2a7a5e" }} />
                        <span className="potw-score-label">Novelty</span>
                        <span className="potw-score-value">{paper.noveltyScore ?? "—"}</span>
                    </div>
                    <div className="potw-score">
                        <span className="potw-score-dot" style={{ background: "#5b8fa8" }} />
                        <span className="potw-score-label">Method</span>
                        <span className="potw-score-value">{paper.methodScore ?? "—"}</span>
                    </div>
                    <div className="potw-score">
                        <span className="potw-score-dot" style={{ background: "#a86b3c" }} />
                        <span className="potw-score-label">Impact</span>
                        <span className="potw-score-value">{paper.impactScore ?? "—"}</span>
                    </div>
                </div>
            </div>
            <div className="potw-aside">
                <div className="potw-overall">
                    <span className="potw-overall-label">Overall</span>
                    <span className="potw-overall-value">{overall}</span>
                </div>
                {isShowcase ? (
                    <button
                        className="potw-btn potw-btn-primary"
                        onClick={(e) => { e.stopPropagation(); onOpen(); }}
                    >
                        <ExternalLink size={13} />
                        View paper
                    </button>
                ) : paper.allowDownload && (
                    <button className="potw-btn potw-btn-primary" onClick={onDownload} disabled={downloading}>
                        <Download size={13} />
                        {downloading ? "Downloading…" : "Download"}
                    </button>
                )}
            </div>
        </div>
    );
}

export default function Home() {
    const navigate = useNavigate();

    const [recentSubmissions, setRecentSubmissions] = useState([]);
    const [totalUploaded, setTotalUploaded] = useState(null);
    const [scoreHistory, setScoreHistory] = useState([]);
    const [chartLoading, setChartLoading] = useState(true);
    const [scoredCount, setScoredCount] = useState(null);
    const [avgScore, setAvgScore] = useState(null);
    const [topScore, setTopScore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [firstName, setFirstName] = useState("");
    const [showScoreHistory, setShowScoreHistory] = useState(true);
    const [paperOfTheWeek, setPaperOfTheWeek] = useState(null);
    const [downloadingPaper, setDownloadingPaper] = useState(false);
    const { isEnabled } = useFeatureFlags();
    const isAdmin = getJwtRole() === admin;
    // Paper of the Week is gated: hidden from regular users when the section is
    // disabled, but admins still see it (flagged as hidden from users).
    const potwEnabled = isEnabled(FEATURES.PAPER_OF_THE_WEEK);
    const potwVisible = !!paperOfTheWeek && (potwEnabled || isAdmin);
    const potwHiddenFromUsers = !!paperOfTheWeek && !potwEnabled && isAdmin;

    useEffect(() => {
        request("GET", "/users/userinfo")
            .then(res => setFirstName(res.firstName ?? ""))
            .catch(() => {});

        fetchUserSettings(true)
            .then(res => {
                const s = res.settings ?? {};
                if (s.showScoreHistory != null) setShowScoreHistory(s.showScoreHistory);
            })
            .catch(() => {});

        fetchPaperOfTheWeek(true)
            .then(res => setPaperOfTheWeek(res?.paperOfTheWeek ?? null))
            .catch(() => {});

        request("GET", "/manuscript/all")
            .then(res => {
                const list = res.manuscripts ?? res ?? [];
                setTotalUploaded(list.length);
                const sorted = [...list].sort((a, b) => (b.uploadDate ?? 0) - (a.uploadDate ?? 0));
                setRecentSubmissions(sorted.slice(0, 5));
            })
            .catch(() => {})
            .finally(() => setLoading(false));

        request("GET", "/manuscript/scored")
            .then(res => {
                const list = res.manuscripts ?? res ?? [];
                setScoredCount(list.length);
                if (list.length > 0) {
                    const scores = list.map(m => calculateOverallScore(m));
                    setAvgScore(Math.round(scores.reduce((a, b) => a + b, 0) / scores.length));
                    setTopScore(Math.max(...scores));
                }
                const sorted = [...list].sort((a, b) => (a.uploadDate ?? 0) - (b.uploadDate ?? 0));
                const history = sorted.slice(-8).map(m => ({
                    name: m.uploadDate ? new Date(m.uploadDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
                    Novelty: m.noveltyScore ?? 0,
                    Methodology: m.methodScore ?? 0,
                    Impact: m.impactScore ?? 0,
                }));
                setScoreHistory(history);
            })
            .catch(() => {})
            .finally(() => setChartLoading(false));
    }, []);

    const displayStats = [
        { label: "Total Uploaded", value: totalUploaded ?? "—" },
        { label: "Papers Analyzed", value: scoredCount ?? "—" },
        { label: "Average Score", value: avgScore ?? "—" },
        { label: "Top Score", value: topScore ?? "—" },
    ];

    return (
        <div className="home-layout">
            <Sidebar activePage="Dashboard" />

            {/* Main */}
            <main className="home-main">
                <Header />

                <div className="home-content">
                    {/* Hero */}
                    <section className="hero-card" style={{ backgroundImage: `url(${CoralReef})` }}>
                        <div className="hero-card-overlay" />
                        <div className="hero-text">
                            <h1 className="hero-heading">Welcome back{firstName ? ` ${firstName}` : ""}</h1>
                            <p className="hero-sub">Upload a new research paper</p>
                            <button className="upload-btn" onClick={() => navigate("/upload")}>
                                <FileUp size={14} />
                                Upload New Paper
                            </button>
                        </div>
                    </section>

                    {/* Stats */}
                    <section className="stats-row">
                        {chartLoading ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="stat-card">
                                    <div className="skeleton" style={{ height: 11, width: "55%", marginBottom: 10 }} />
                                    <div className="skeleton" style={{ height: 28, width: "38%", marginBottom: 8 }} />
                                    <div className="skeleton" style={{ height: 10, width: "68%" }} />
                                </div>
                            ))
                        ) : displayStats.map((s) => (
                            <div key={s.label} className="stat-card">
                                <p className="stat-label">{s.label}</p>
                                <p className="stat-value">{s.value}</p>
                            </div>
                        ))}
                    </section>

                    {/* Score History + Paper of the Week sidebar */}
                    {(showScoreHistory || potwVisible) && <section className="chart-potw-row">
                    {showScoreHistory && <section className="score-history-card">
                        <div className="score-history-header">
                            <div>
                                <p className="score-history-title">Score History</p>
                                <p className="score-history-sub">Novelty, methodology & impact across your submissions</p>
                            </div>
                            {!chartLoading && scoreHistory.length > 0 && (
                                <div className="score-history-legend">
                                    <span className="legend-dot" style={{ background: "var(--accent-text)" }} />Novelty
                                    <span className="legend-dot" style={{ background: "var(--series-blue)" }} />Methodology
                                    <span className="legend-dot" style={{ background: "var(--bronze)" }} />Impact
                                </div>
                            )}
                        </div>
                        {chartLoading ? (
                            <div className="skeleton" style={{ height: 200, borderRadius: 8, marginTop: 8 }} />
                        ) : scoreHistory.length === 0 ? (
                            <div style={{ height: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
                                <p className="settings-empty">No scored submissions yet.</p>
                                <div style={{ display: "flex", gap: 10 }}>
                                    <button className="upload-btn" onClick={() => navigate("/upload")}>
                                        <FileUp size={13} />
                                        Upload a Paper
                                    </button>
                                    <button className="upload-btn" style={{ background: "transparent", color: "var(--ink-600)", border: "1px solid var(--border)" }} onClick={() => navigate("/sample/submission")}>
                                        View Sample Report
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={scoreHistory} margin={{ top: 8, right: 32, left: -20, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <YAxis domain={[50, 100]} tick={{ fontSize: 11, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                                        cursor={{ stroke: "var(--border)" }}
                                    />
                                    <Line type="monotone" dataKey="Novelty" stroke="var(--accent-text)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                    <Line type="monotone" dataKey="Methodology" stroke="var(--series-blue)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                    <Line type="monotone" dataKey="Impact" stroke="var(--bronze)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </section>}
                    {potwVisible && (
                        <PaperOfTheWeekCard
                            paper={paperOfTheWeek}
                            downloading={downloadingPaper}
                            hiddenFromUsers={potwHiddenFromUsers}
                            onOpen={() => paperOfTheWeek.showcaseKey && navigate(`/showcase/${paperOfTheWeek.showcaseKey}`)}
                            onDownload={async () => {
                                if (downloadingPaper) return;
                                setDownloadingPaper(true);
                                try {
                                    const { blob, disposition } = await downloadFeaturedManuscript(paperOfTheWeek.id, true);
                                    const match = disposition && disposition.match(/filename="?([^"]+)"?/);
                                    const filename = match ? match[1] : "paper.pdf";
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = filename;
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                } catch {
                                    // swallow download errors silently
                                } finally {
                                    setDownloadingPaper(false);
                                }
                            }}
                        />
                    )}
                    </section>}

                    {/* Recent Submissions (Community Feed temporarily hidden) */}
                    {/* TODO: remove inline gridTemplateColumns override when CommunityFeed is re-enabled below */}
                    {!loading && recentSubmissions.length > 0 && <section className="bottom-section" style={{ gridTemplateColumns: "1fr" }}>
                        <div className="submissions-card">
                            <div className="submissions-header">Recent Submissions</div>
                            <table className="submissions-table recent-submissions-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Status</th>
                                        <th>Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentSubmissions.map((item) => (
                                        <tr key={item.id} className="submission-row" onClick={() => navigate(`/submission?id=${item.id}`)} style={{ cursor: "pointer" }}>
                                            <td>
                                                <p className="submission-title">{item.title || item.name}</p>
                                                <p className="submission-authors">{item.authors}</p>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${statusClass(item.status)}`}>
                                                    <StatusIcon status={item.status} />
                                                    <span className="status-label-full">{statusLabel(item.status)}</span>
                                                    <span className="status-label-short">{statusLabel(item.status)[0]}</span>
                                                </span>
                                            </td>
                                            <td>
                                                {item.status?.toLowerCase() === "completed"
                                                    ? (() => { const s = calculateOverallScore(item); return s > 0 ? <span className="score-badge">{s}</span> : "—"; })()
                                                    : <span className="score-dash">—</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* <CommunityFeed /> */}
                    </section>}

                    {/* Feed full-width when there are no submissions */}
                    {/* {!loading && recentSubmissions.length === 0 && <CommunityFeed />} */}
                </div>
            </main>
        </div>
    );
}
